/* eslint-disable @typescript-eslint/no-magic-numbers */
/* eslint-disable @angular-eslint/no-input-rename */
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  ChangeDetectionStrategy,
  Input,
  EventEmitter,
  Output,
  inject,
  signal,
  WritableSignal,
  Signal,
} from '@angular/core';
import { Params } from '@angular/router';
import { configureScope } from '@sentry/browser';
import { Subscription, Observable, Subject, takeUntil, timer, take, map } from 'rxjs';
import { MetaService } from '@app/shared/services/meta.service';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { WindowUtils } from '@app/shared/utils/window.util';
import {
  Validators,
  UntypedFormControl,
  ReactiveFormsModule,
  FormControl,
} from '@angular/forms';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { GoogleAnalyticsService } from '@app/core/services/google-analytics.service';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import {
  logLogin,
  logVerifySMSConfirmed,
  logVerifySMSFailed,
} from '@app/core/services/google-analytics.lazy';
import { AkitaCartService } from '@app/akita/cart/state/cart.service';
import { LoadingComponent } from '../../../shared/components/loading/loading.component';
import { NgIf } from '@angular/common';
import { FirebaseAuthService } from '@app/akita/firebase/services/firebase-auth.service';
import { AkitaFirebaseQuery } from '@app/akita/firebase/state/firebase.query';
import { PopsyPhoneInputComponent } from '../../../components/inputs/phone-input/popsy-phone-input.component';
import { PopsyInputComponent } from '../../../components/inputs/input/popsy-input.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { ConfirmationResult, UserCredential } from 'firebase/auth';
import { parseApiError } from '@app/shared/models/api/api-error.model';
// eslint-disable-next-line max-len
import { EnvelopeIconComponent } from '../../../shared/components/icons/generated/envelope/envelope.component';

@Component({
  selector: 'app-email-phone-sign-in',
  templateUrl: './email-phone-sign-in.template.html',
  styleUrls: ['./email-phone-sign-in.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoModule,
    NgIf,
    ReactiveFormsModule,
    LoadingComponent,
    PopsyPhoneInputComponent,
    PopsyInputComponent,
    EnvelopeIconComponent,
  ],
})
export class EmailPhoneSignInComponent implements OnDestroy {
  @Input() public inDialog?: boolean | null = null;

  @Input('success-path') public successPath?: Array<string> | null = null;
  @Input('success-params') public successRedirectQueryParams?: Params | null = null;
  @Input('skip-redirect') public skipRedirect?: boolean | null = null;

  @Output() public readonly handleRedirect: EventEmitter<void> = new EventEmitter();
  @Output() public readonly dialogModeChange: EventEmitter<any> = new EventEmitter();
  @Output() public readonly closeDialog: EventEmitter<boolean> = new EventEmitter();
  @Output() public readonly submitting: EventEmitter<boolean> = new EventEmitter();

  public logInputFormControl: UntypedFormControl;

  public formValue: WritableSignal<string> = signal<string>('');
  public formValueMode: WritableSignal<'email' | 'phone'> = signal<'email' | 'phone'>(
    'email'
  );

  public otpRequestedResults: WritableSignal<ConfirmationResult | null> = signal(null);
  public otpForm: FormControl;

  public keepFocus: WritableSignal<boolean> = signal<boolean>(false);
  public defaultCountryCode: Signal<string>;

  public redirect?: string | null = null;
  public hidePassword: boolean;
  public submitted: boolean;
  public failed: boolean;
  public success: boolean;
  public loading: boolean;
  public isBrowser: boolean;
  public params: Params = {};
  public queryParams: Params = {};
  public routeData: Params = {};

  public lang?: string;

  private readonly langChangesObserver: Subscription | null;
  private readonly userLoginObserver?: Subscription | null;
  private readonly subscriptions: Subscription;
  private readonly subs: Subscription[] = [];

  public lessThan$: Observable<ScreenSizeInfoState>;
  public isRtlLayout$: Observable<boolean>;

  private readonly destroySubject: Subject<void>;
  public showCountryPicker = false;

  public requestingAgainCounterdown: WritableSignal<number> = signal(0);
  public retryOTP: WritableSignal<boolean> = signal(false);

  public counter$: Observable<void>;

  private readonly translateService = inject(TranslocoService);

  constructor(
    private readonly meta: MetaService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly firebaseAuthService: FirebaseAuthService,
    private readonly akitaFirebaseQuery: AkitaFirebaseQuery,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly googleAnalyticsService: GoogleAnalyticsService,
    private readonly akitaCartService: AkitaCartService,
    private readonly translationService: TranslocoService,
    private readonly analytics: GoogleAnalyticsService
  ) {
    this.destroySubject = new Subject<void>();
    this.subscriptions = new Subscription();
    this.isBrowser = this.akitaRouterQuery.isBrowser;
    this.hidePassword = true;
    this.submitted = false;
    this.loading = false;
    this.success = false;
    this.failed = false;

    this.defaultCountryCode = toSignal(this.akitaRouterQuery.selectCountry(), {
      initialValue: 'SA',
    });

    this.counter$ = new Observable<void>();

    this.lang = this.translationService.getActiveLang();

    this.lessThan$ = this.akitaScreenQuery.selectLessThan();
    this.isRtlLayout$ = this.akitaRouterQuery.selectIsRtlLayout();

    this.logInputFormControl = new UntypedFormControl('', {
      validators: Validators.compose([Validators.required, Validators.email]),
      updateOn: 'change',
    });

    this.otpForm = new UntypedFormControl('', {
      validators: Validators.compose([Validators.required]),
      updateOn: 'change',
    });

    this.langChangesObserver = this.translateService.langChanges$
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: () => {
          const description = this.translateService.translate('app_title');
          this.meta.setTitle('title_login', true);
          this.meta.setCanonical();
          this.meta.setAlternateHreflangsTags(null);

          this.meta.setTags([
            { name: 'og:url', content: `${WindowUtils.getLocationHref() || ''}` },
            { name: 'og:type', content: `website` },
            { name: 'og:description', content: description },
            { name: 'description', content: description },
          ]);
        },
        error: () => {},
      });
  }

  public goBack(): void {
    this.submitted = false;
    this.otpRequestedResults.set(null);
    this.submitting.emit(false);
  }

  public submitEmailPhoneValue(): void {
    if (this.formValueMode() === 'email') {
      // verify email format
      this.logInputFormControl.setValidators([Validators.required, Validators.email]);
    } else if (this.formValueMode() === 'phone') {
      // verify phone format
      this.logInputFormControl.setValidators([
        Validators.required,
        Validators.minLength(8),
      ]);
    }
    this.logInputFormControl.setValue(this.formValue());
    this.logInputFormControl.updateValueAndValidity();

    if (this.logInputFormControl.status !== 'VALID') {
      console.warn(
        'Form is not valid, cannot submit email or phone value',
        this.logInputFormControl.value
      );
      return;
    }
    if (this.formValueMode() === 'email') {
      this.emailSignInWithFirebaseEmail();
    } else if (this.formValueMode() === 'phone') {
      this.sendCodeWithPhoneNumber();
    }
  }

  public emailSignInWithFirebaseEmail(): void {
    if (this.logInputFormControl.status !== 'VALID') {
      return;
    }

    const emailField = this.logInputFormControl.value;

    this.loading = true;
    this.submitted = true;
    this.failed = false;
    this.success = false;

    this.submitting.emit(true);

    configureScope((scope) => {
      scope.setUser({
        id: '?',
        username: '',
        email: emailField,
      });
    });

    this.subscriptions.add(
      this.firebaseAuthService
        .sendSignInWithEmailLink(emailField)
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: () => {
            this.loading = false;
            this.changeDetector.markForCheck();

            try {
              // In the tab where user starts the email-phone-sign-in
              const bc = new BroadcastChannel('auth');
              bc.onmessage = (e) => {
                if (e.data === 'signInSuccess') {
                  // e.g., reload UI or fetch fresh user data
                  setTimeout(() => {
                    const user = this.akitaFirebaseQuery.user;
                    if (user) {
                      this.subscriptions.add(
                        this.akitaCartService.transferAnonymousCartAsync()
                      );
                      logLogin(this.googleAnalyticsService.auth, user, 'email');
                      this.firebaseAuthService.refreshAuth();
                      this.handleRedirect.next();
                      this.close(false);
                    } else {
                      this.firebaseAuthService.refreshAuth();
                      console.error('No user found after Firebase email-phone-sign-in');
                    }
                    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
                  }, 5000);
                }
              };
            } catch (err) {}
          },
          error: () => {
            this.failed = true;
            this.submitted = false;
            this.loading = false;
            this.submitting.emit(false);
            this.changeDetector.markForCheck();
          },
        })
    );
  }

  public sendCodeWithPhoneNumber(): void {
    this.loading = true;
    this.submitting.emit(true);
    const sub = this.firebaseAuthService
      .signInWithPhoneNumber(this.logInputFormControl.value || '', this.retryOTP())
      .subscribe({
        next: (confirmationResult: ConfirmationResult) => {
          this.loading = false;
          this.otpRequestedResults.set(confirmationResult);
          this.reStartCounter();
          this.retryOTP.set(true);
        },
        error: (err: unknown) => {
          console.error('Error signing in with phone number:', err);
          this.submitting.emit(false);
          this.submitted = false;
          this.reStartCounter();

          this.loading = false;
        },
      });

    this.subs.push(sub);
  }

  public confirmCode(): void {
    const otp = this.otpForm.value;
    if (!this.otpRequestedResults) {
      return;
    }

    this.loading = true;
    if (this.otpRequestedResults() !== null) {
      const sub = this.firebaseAuthService
        .confirmOtp(this.otpRequestedResults(), otp)
        .subscribe({
          next: (userCred: UserCredential) => {
            this.loading = false;
            if (userCred) {
              setTimeout(() => {
                const user = this.akitaFirebaseQuery.user;
                logLogin(this.analytics.auth, user, 'phone number');
                logVerifySMSConfirmed(this.analytics.logEventWrapper);

                this.handleRedirect.next();
                this.closeDialog.emit(false);
                // eslint-disable-next-line @typescript-eslint/no-magic-numbers
              }, 2000);
            }
          },
          error: (err: unknown) => {
            const apiError = parseApiError(err);
            this.loading = false;
            logVerifySMSFailed(
              this.analytics.logEventWrapper,
              'Error confirming OTP:',
              apiError.message,
              'phone-sign-in'
            );
            console.error('Error confirming OTP:', err);
          },
        });

      this.subs.push(sub);
    }
  }

  public getMaskedNumber(): string | null {
    if (!this.logInputFormControl.value) {
      return null;
    }
    return this.logInputFormControl.value.replace(/.(?=.{3,}$)/g, (match: string) =>
      match === ' ' ? ' ' : 'X'
    );
  }

  public getLastFourDigits(): string | null {
    if (!this.logInputFormControl.value) {
      return null;
    }
    const phoneNumber = this.logInputFormControl.value;
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    return digitsOnly.slice(-4);
  }

  public changeDialogMode(mode?: string | null): boolean {
    const username: string | null =
      this.params.username || this.queryParams.username || null;
    let email: string | null = null;
    const emailField = this.logInputFormControl.value;
    if (emailField && emailField.status === 'VALID') {
      email = emailField.value;
    }

    this.dialogModeChange.emit({
      mode: mode || 'email-phone-sign-in',
      email: email,
      username: username,
      previousMode: 'auth-intro',
    });

    return false;
  }

  public onFormValueChange(phone: any): void {
    const startsWithNumber = /^[0-9+]/;

    this.formValue.set(typeof phone === 'string' ? phone : phone?.display || '');
    if (
      (typeof phone === 'string' && !startsWithNumber.test(phone)) ||
      phone === null ||
      phone === undefined
    ) {
      this.formValueMode.set('email');
    } else if (
      (typeof phone === 'string' && startsWithNumber.test(phone)) ||
      (typeof phone === 'object' &&
        typeof phone.national === 'string' &&
        startsWithNumber.test(phone?.national))
    ) {
      this.formValueMode.set('phone');
    }
  }

  public reStartCounter(): void {
    if (this.requestingAgainCounterdown() === 0) {
      this.requestingAgainCounterdown.set(60);

      this.counter$ = timer(0, 1000).pipe(
        take(60),
        map(() => {
          if (this.requestingAgainCounterdown() > 0) {
            this.requestingAgainCounterdown.set(this.requestingAgainCounterdown() - 1);
          }
        })
      );

      this.subscriptions.add(
        this.counter$.subscribe({
          complete: () => {
            this.requestingAgainCounterdown.set(0);
          },
        })
      );
    }
  }

  public close(dismiss?: boolean): boolean {
    this.closeDialog.emit(dismiss);
    return false;
  }

  public stopPropagation(): boolean {
    return false;
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }

    if (this.userLoginObserver) {
      this.userLoginObserver.unsubscribe();
    }

    if (this.langChangesObserver) {
      this.langChangesObserver.unsubscribe();
    }
  }
}
